import type {
  BulkInvoiceResult,
  InvoiceBulkFilter,
  InvoiceBulkSendQueued,
  InvoiceBulkSendResult,
  InvoiceBulkTarget,
  InvoiceStatusCounts,
} from '@clockwise/clients/invoices';
import type { InvoiceStatus } from '@clockwise/shared';

/**
 * What the invoice list's bulk bar is holding, and the arithmetic over it.
 *
 * Pure and separate from the route so the part that is easy to get wrong — how
 * many rows an action would actually touch — can be tested without a table, a
 * router or a server.
 */

/** Selected rows carry their status: a selection spans pages, and the bar has
 *  to say how many of it are drafts long after those rows scrolled away. */
export type SelectedRows = ReadonlyMap<string, InvoiceStatus>;

/**
 * Two modes, because "these three rows" and "all 1,842 matching" are different
 * claims and only one of them fits in a list of ids.
 *
 * `filter` carries the rows unticked *after* selecting everything — the small
 * exception list, rather than the enormous inclusion list.
 */
export type InvoiceSelection =
  | { mode: 'ids'; rows: SelectedRows }
  | { mode: 'filter'; excluded: SelectedRows };

export const NOTHING_SELECTED: InvoiceSelection = { mode: 'ids', rows: new Map() };

/**
 * Which statuses this person's actions can reach, so only those rows tick.
 * A row no action can touch would sit in the count while every action
 * silently skipped it.
 *
 *   - `drafts`: Approve and Invoice date act on drafts — someone who may
 *     approve or edit.
 *   - `sent`: `sent` is terminal; the one thing still possible is sending it
 *     again — someone who may send.
 *
 * Approved rows are always selectable (Send date, Send).
 */
export interface SelectionRules {
  drafts: boolean;
  sent: boolean;
}

export const DEFAULT_SELECTION_RULES: SelectionRules = { drafts: true, sent: false };

export function isSelectable(
  status: string,
  rules: SelectionRules = DEFAULT_SELECTION_RULES,
): boolean {
  return (
    status === 'approved' ||
    (rules.drafts && status === 'draft') ||
    (rules.sent && status === 'sent')
  );
}

export function idsWithStatus(rows: SelectedRows, status: InvoiceStatus): string[] {
  return [...rows].filter(([, s]) => s === status).map(([id]) => id);
}

export function countWithStatus(rows: SelectedRows, status: InvoiceStatus): number {
  return idsWithStatus(rows, status).length;
}

/** A row for selection purposes — everything the bar needs and nothing else. */
export interface SelectableRow {
  id: string;
  status: string;
}

export function isRowSelected(
  selection: InvoiceSelection,
  row: SelectableRow,
  rules: SelectionRules = DEFAULT_SELECTION_RULES,
): boolean {
  return selection.mode === 'filter'
    ? // A row no action can touch is never shown ticked under a filter
      // selection: a tick that means nothing is worse than no tick.
      isSelectable(row.status, rules) && !selection.excluded.has(row.id)
    : selection.rows.has(row.id);
}

/** Tick or untick one row. Under a filter selection this narrows the set rather
 *  than abandoning it — the whole point of the exclusion list. */
export function toggleRow(
  selection: InvoiceSelection,
  row: SelectableRow,
  next: 'all' | 'none',
): InvoiceSelection {
  if (selection.mode === 'filter') {
    const excluded = new Map(selection.excluded);
    if (next === 'all') excluded.delete(row.id);
    else excluded.set(row.id, row.status as InvoiceStatus);
    return { mode: 'filter', excluded };
  }
  const rows = new Map(selection.rows);
  if (next === 'all') rows.set(row.id, row.status as InvoiceStatus);
  else rows.delete(row.id);
  return { mode: 'ids', rows };
}

/** Select every selectable row on the page. */
export function selectPage(
  rows: readonly SelectableRow[],
  rules: SelectionRules = DEFAULT_SELECTION_RULES,
): InvoiceSelection {
  return {
    mode: 'ids',
    rows: new Map(
      rows
        .filter((r) => isSelectable(r.status, rules))
        .map((r) => [r.id, r.status as InvoiceStatus]),
    ),
  };
}

/** How much of the page the header checkbox covers. */
export function pageSelectionState(
  selection: InvoiceSelection,
  rows: readonly SelectableRow[],
  rules: SelectionRules = DEFAULT_SELECTION_RULES,
): 'none' | 'some' | 'all' {
  const selectable = rows.filter((r) => isSelectable(r.status, rules));
  if (selection.mode === 'filter') {
    // A filter selection covers the page — except for whatever was unticked on
    // it. Reporting 'all' regardless would put a fully-checked header directly
    // above rows rendering unchecked. Never 'none': even a page whose every row
    // was unticked is part of a selection that still reaches other pages.
    return selectable.some((r) => selection.excluded.has(r.id)) ? 'some' : 'all';
  }
  if (selectable.length > 0 && selectable.every((r) => selection.rows.has(r.id))) return 'all';
  return selectable.some((r) => selection.rows.has(r.id)) ? 'some' : 'none';
}

/**
 * How many rows each action would actually touch.
 *
 * Never "how many are selected": a selection of 30 invoices holding 12 drafts
 * approves 12, and a button that said 30 would promise something the server
 * will not do. Under a filter selection the numbers come from the server's own
 * counts over the same filter, less whatever was unticked afterwards.
 *
 *   - approvable: drafts (Approve, Invoice date)
 *   - schedulable: drafts and approved (Send date)
 *   - sendable: approved and sent (Send)
 *
 * Drafts and sent rows only count — and only make up `selected` — when the
 * rules make them selectable, matching `isSelectable`.
 */
export function actionCounts(
  selection: InvoiceSelection,
  matching: InvoiceStatusCounts | undefined,
  rules: SelectionRules = DEFAULT_SELECTION_RULES,
): {
  approvable: number;
  approved: number;
  sent: number;
  schedulable: number;
  sendable: number;
  selected: number;
} {
  const tally = (approvable: number, approved: number, sent: number) => ({
    approvable,
    approved,
    sent,
    schedulable: approvable + approved,
    sendable: approved + sent,
    selected: approvable + approved + sent,
  });
  if (selection.mode === 'ids') {
    return tally(
      countWithStatus(selection.rows, 'draft'),
      countWithStatus(selection.rows, 'approved'),
      countWithStatus(selection.rows, 'sent'),
    );
  }
  // Undefined counts mean the read is still in flight. Zero is the honest
  // stand-in: it disables every action until the number is known, rather than
  // offering to act on an amount nobody has established yet.
  const left = (status: InvoiceStatus) =>
    Math.max(0, (matching?.[status] ?? 0) - countWithStatus(selection.excluded, status));
  return tally(rules.drafts ? left('draft') : 0, left('approved'), rules.sent ? left('sent') : 0);
}

/** The ids-or-filter target for the selected rows in any of `statuses`. Under
 *  a filter only those statuses' exclusions are sent: the server constrains by
 *  status anyway, and a shorter list stays under the cap. */
export function targetForStatuses(
  selection: InvoiceSelection,
  filter: InvoiceBulkFilter,
  statuses: readonly InvoiceStatus[],
): InvoiceBulkTarget {
  const pick = (rows: SelectedRows) =>
    [...rows].filter(([, s]) => statuses.includes(s)).map(([id]) => id);
  return selection.mode === 'filter'
    ? { filter, excludeIds: pick(selection.excluded) }
    : { ids: pick(selection.rows) };
}

/** The server caps an id list at 500 per call. A selection ticked across pages
 *  can exceed that, so it goes in chunks — each its own transaction, which the
 *  per-row result contract already accommodates. */
export const ID_CHUNK = 500;

/**
 * Run one bulk call over a target, splitting an over-cap id list into chunks
 * and merging the per-row results, rather than failing a selection the UI let
 * the user build. A filter target is one call: the server resolves it.
 *
 * Shared by the real write and its dry run, so a preview can never cover a
 * different set of rows than the action it previews.
 */
export async function runBulkInChunks(
  target: InvoiceBulkTarget,
  call: (target: InvoiceBulkTarget) => Promise<{ data: BulkInvoiceResult }>,
): Promise<{ data: BulkInvoiceResult }> {
  if (!('ids' in target) || target.ids.length <= ID_CHUNK) return call(target);
  const merged: BulkInvoiceResult = { succeeded: [], failed: [] };
  for (let i = 0; i < target.ids.length; i += ID_CHUNK) {
    const { data } = await call({ ids: target.ids.slice(i, i + ID_CHUNK) });
    merged.succeeded.push(...data.succeeded);
    merged.failed.push(...data.failed);
  }
  return { data: merged };
}

/** Split an over-cap id list into chunks, one call each, folding the answers
 *  with `merge`; a filter target is one call (the server resolves it). */
async function runInIdChunks<T>(
  target: InvoiceBulkTarget,
  call: (target: InvoiceBulkTarget) => Promise<{ data: T }>,
  merge: (into: T, next: T) => T,
): Promise<{ data: T }> {
  if (!('ids' in target) || target.ids.length <= ID_CHUNK) return call(target);
  let merged: T | undefined;
  for (let i = 0; i < target.ids.length; i += ID_CHUNK) {
    const { data } = await call({ ids: target.ids.slice(i, i + ID_CHUNK) });
    merged = merged === undefined ? data : merge(merged, data);
  }
  return { data: merged as T };
}

/** `runBulkInChunks` for the bulk send's preview: the same chunking, so a
 *  preview covers exactly the rows the send will. */
export function runBulkSendInChunks(
  target: InvoiceBulkTarget,
  call: (target: InvoiceBulkTarget) => Promise<{ data: InvoiceBulkSendResult }>,
): Promise<{ data: InvoiceBulkSendResult }> {
  return runInIdChunks(target, call, (a, b) => ({
    send: [...a.send, ...b.send],
    skipped: [...a.skipped, ...b.skipped],
  }));
}

/** The same chunking for queuing the send itself. */
export function runBulkQueueInChunks(
  target: InvoiceBulkTarget,
  call: (target: InvoiceBulkTarget) => Promise<{ data: InvoiceBulkSendQueued }>,
): Promise<{ data: InvoiceBulkSendQueued }> {
  return runInIdChunks(target, call, (a, b) => ({
    queued: a.queued + b.queued,
    skipped: [...a.skipped, ...b.skipped],
  }));
}

/**
 * Why the skipped rows were skipped, as a line a person can act on.
 *
 * Distinct REASONS, not the first three rows. A bulk that fails for one reason
 * fails for it many times over — an interrupted run hands back a thousand
 * invoices carrying one identical sentence — and printing that sentence three
 * times reads as a stutter rather than as information. How MANY were skipped is
 * already on the line above this one; what this line adds is what went wrong.
 *
 * Still capped at three: a toast listing two hundred distinct reasons is a
 * toast nobody reads.
 */
export function summarizeFailures(failed: ReadonlyArray<{ message: string }>): string {
  const distinct = [...new Set(failed.map((f) => f.message))];
  const shown = distinct.slice(0, 3);
  const rest = distinct.length - shown.length;
  return rest > 0
    ? `${shown.join(' ')} …and ${rest} more reason${rest === 1 ? '' : 's'}.`
    : shown.join(' ');
}

/**
 * What a dry run says will happen, split the way the dialog warns about it.
 *
 * Send-date conflicts are called out on their own, by count: those invoices
 * keep their current date AND still email on their scheduled day, which is the
 * outcome most likely to surprise someone who only read "Update 12 invoices".
 * Everything else skipped (locked, no access) is summarised by reason.
 */
export function invoiceDatePreviewSummary(result: BulkInvoiceResult): {
  willChange: number;
  sendConflicts: number;
  otherSkips: BulkInvoiceResult['failed'];
} {
  const sendConflicts = result.failed.filter((f) => f.reason === 'SEND_BEFORE_INVOICE_DATE');
  return {
    willChange: result.succeeded.length,
    sendConflicts: sendConflicts.length,
    otherSkips: result.failed.filter((f) => f.reason !== 'SEND_BEFORE_INVOICE_DATE'),
  };
}
